import { all, call, put, fork, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from 'types';

import { inboxPageActions as actions, SendMessagesFailurePayload } from '.';
import {
  GetTemplateLinkType,
  SendMessagesType,
  UpdateClientAttributesAndTagsPayload,
} from './types';
import { AddContactsType } from 'app/pages/Users/slice/types';

import api from 'app/api';
import { getBusinessIdFromToken } from 'utils/getBusinessId';
import { successModal, errorModal } from 'utils/showSuccessErrorModal';
import { getCachedTemplates, setCachedTemplates } from 'utils/indexedDB';
import { CustomError } from 'utils/types/success-error-types';

const toCustomError = (error: unknown): CustomError =>
  (error as AxiosError<CustomError>)?.response?.data ?? ({} as CustomError);

/* Clients APIs */
const INITIAL_CLIENT_PAGE_SIZE = 1000;
const LOAD_MORE_CLIENT_PAGE_SIZE = 500;

export function* getClientListPage(
  action: PayloadAction<{ limit?: number } | undefined>,
) {
  try {
    const state1 = yield select((state: RootState) => state.authPage);
    const limit = action?.payload?.limit ?? INITIAL_CLIENT_PAGE_SIZE;
    const response = yield call(api.get, `/clients?limit=${limit}`);
    yield put(
      actions.fetchClientListPageSuccess({
        data: response.data.data.clients,
        nextCursor: response.data.data.nextCursor ?? null,
        usernameMasking: state1?.permission?.contactNumberMasking,
      }),
    );
  } catch (error) {
    yield put(actions.fetchClientListPageFailure(toCustomError(error)));
  }
}

export function* loadMoreClientList() {
  try {
    const cursor = yield select(
      (state: RootState) => state.inboxPage?.clientListNextCursor,
    );
    if (!cursor) {
      yield put(
        actions.loadMoreClientListSuccess({ data: [], nextCursor: null }),
      );
      return;
    }
    const url = `/clients?limit=${LOAD_MORE_CLIENT_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`;
    const response = yield call(api.get, url);
    yield put(
      actions.loadMoreClientListSuccess({
        data: response.data.data.clients,
        nextCursor: response.data.data.nextCursor ?? null,
      }),
    );
  } catch (error) {
    yield put(actions.loadMoreClientListFailure(toCustomError(error)));
  }
}

export function* addContacts(
  action: PayloadAction<AddContactsType[] | unknown>,
) {
  try {
    const response = yield call(api.post, `/clients`, action.payload);
    yield put(actions.addContactsSuccess(response.data.data));
    successModal(response?.data?.message || 'Contact updated successfully');
  } catch (err) {
    const customError = toCustomError(err);
    yield put(actions.addContactsFailure(customError));
    errorModal(customError?.errorMessage || 'Failed to update contact');
  }
}

export function* updateClientAttributesAndTags(
  action: PayloadAction<UpdateClientAttributesAndTagsPayload[]>,
) {
  try {
    const response = yield call(
      api.put,
      `/clients/attributes-and-tags`,
      action.payload,
    );
    yield put(actions.updateClientAttributesAndTagsSuccess(response.data.data));
    successModal('Updated successfully');
  } catch (err) {
    const customError = toCustomError(err);
    yield put(actions.updateClientAttributesAndTagsFailure(customError));
    errorModal(customError?.errorMessage || 'Failed to update');
  }
}

export function* toggleChat(
  action: PayloadAction<{ clientWaNumber: string; isOpen: boolean }>,
) {
  const { clientWaNumber, isOpen } = action.payload;
  try {
    yield call(api.put, `/clients/chat/toggle/${clientWaNumber}`, { isOpen });
    yield put(actions.toggleClientSuccess({ clientWaNumber, result: isOpen }));
    successModal(isOpen ? 'Chat opened' : 'Chat closed');
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.toggleClientFailure(customError));
    errorModal(customError?.errorMessage || 'Failed to toggle chat');
  }
}

export function* toggleBotForClient(
  action: PayloadAction<{ clientWaNumber: string; isBotReply: boolean }>,
) {
  const { clientWaNumber, isBotReply } = action.payload;
  try {
    yield call(api.put, `/clients/bot/toggle/${clientWaNumber}`, {
      isBotReply,
    });
    yield put(
      actions.toggleBotForClientSuccess({ clientWaNumber, result: isBotReply }),
    );
    successModal(isBotReply ? 'Bot enabled' : 'Bot disabled');
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.toggleBotForClientFailure(customError));
    errorModal(customError?.errorMessage || 'Failed to toggle bot');
  }
}
export function* toggleBlockClient(
  action: PayloadAction<{ clientWaNumber: string; isBlocked: boolean }>,
) {
  const { clientWaNumber, isBlocked } = action.payload;
  try {
    yield call(api.put, `/clients/chat/block/${clientWaNumber}`, {
      isBlocked,
    });
    yield put(
      actions.toggleBlockClientSuccess({ clientWaNumber, result: isBlocked }),
    );
    successModal(isBlocked ? 'Client blocked' : 'Client unblocked');
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CustomError>;
    yield put(
      actions.toggleBlockClientFailure(
        axiosError?.response?.data ?? ({} as CustomError),
      ),
    );
    errorModal(
      axiosError?.response?.data?.errorMessage ||
        'Failed to toggle block status',
    );
  }
}

export function* clearSession(
  action: PayloadAction<{ clientWaNumber: string }>,
) {
  const { clientWaNumber } = action.payload;
  try {
    const response = yield call(
      api.put,
      `/clients/session/clear/${clientWaNumber}`,
    );
    yield put(
      actions.clearSessionSuccess({
        clientWaNumber,
        sessionClearedAt: response.data.data.sessionClearedAt,
        message: response.data.data.message,
      }),
    );
    successModal('Bot session cleared');
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CustomError>;
    yield put(
      actions.clearSessionFailure(
        axiosError?.response?.data ?? ({} as CustomError),
      ),
    );
    errorModal(
      axiosError?.response?.data?.errorMessage || 'Failed to clear session',
    );
  }
}

/* Messages APIs */
export function* getMessages(
  action: PayloadAction<{
    clientWaNumber: string | undefined;
    isFromMessages?: boolean;
  }>,
) {
  const { clientWaNumber, isFromMessages } = action.payload;
  try {
    if (isFromMessages) {
      const response = yield call(api.get, `/messages/${clientWaNumber}`);
      yield put(actions.fetchMessagesSuccess(response.data.data));
    } else {
      const responseLimit = yield call(
        api.get,
        `/messages/${clientWaNumber}?limit=100`,
      );
      yield put(actions.fetchMessagesSuccess(responseLimit.data.data));
      if (responseLimit.data.data.messages.length >= 100) {
        const response = yield call(api.get, `/messages/${clientWaNumber}`);
        yield put(actions.fetchMessagesSuccess(response.data.data));
      }
    }
  } catch (error) {
    yield put(actions.fetchMessagesFailure(toCustomError(error)));
  }
}

export function* sendMessages(action: PayloadAction<SendMessagesType>) {
  try {
    const response = yield call(api.post, `/messages/send`, {
      messages: action.payload,
    });
    yield put(actions.sendMessagesFailure(response.data.data));
  } catch (err) {
    yield put(
      actions.sendMessagesFailure(
        (err as AxiosError<SendMessagesFailurePayload>)?.response?.data,
      ),
    );
  }
}

export function* getSingleClient(
  action: PayloadAction<{ clientWaNumber: string }>,
) {
  const { clientWaNumber } = action.payload;
  try {
    const response = yield call(api.get, `/clients/${clientWaNumber}`);
    const client = { ...response.data.data, messages: [], isLastMessage: true };
    yield put(actions.setSelectedClient(client));
    yield put(actions.fetchMessagesStart({ clientWaNumber }));
  } catch {
    yield put(actions.selectClientByUrl({ clientWaNumber }));
  }
}

export function* addTemplateLink(action: PayloadAction<GetTemplateLinkType>) {
  try {
    yield call(api.post, `/templates/media-link`, { ...action.payload });
  } catch (error) {
    console.error('addTemplateLink error:', toCustomError(error));
  }
}

export function* bulkAddTemplateMediaLink(
  action: PayloadAction<GetTemplateLinkType & { targetBusinessId?: number }>,
) {
  try {
    const { targetBusinessId, ...payload } = action.payload;
    // Copy-to-business: ?business_id= re-scopes the request to the target so the
    // media link is stored against that business (checkJwt does the swap).
    const query = targetBusinessId ? `?business_id=${targetBusinessId}` : '';
    yield call(api.post, `/templates/bulk-media-link${query}`, payload);
  } catch (error) {
    console.error('bulkAddTemplateMediaLink error:', toCustomError(error));
  }
}

export function* getTemplateLink() {
  try {
    const response = yield call(api.get, `/templates/media-link`);
    yield put(actions.getTemplateMediaLinkSuccess(response.data.data));
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.getTemplateMediaLinkFailure(customError));
    errorModal(customError?.errorMessage || 'Failed to fetch template links');
  }
}

export function* getTemplates(
  action: PayloadAction<{ refresh?: boolean } | undefined>,
) {
  const refresh = action.payload?.refresh;
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(
      api.get,
      refresh ? '/templates?refresh=true' : '/templates',
    );
    const templates = response.data.data;

    // Persist templates to IndexedDB (non-blocking)
    setCachedTemplates(String(businessId), templates);

    // Migrate: remove old localStorage cache if present
    try {
      localStorage.removeItem(`templates_${businessId}`);
    } catch {
      // ignore
    }

    yield put(actions.getTemplatesSuccess(templates));
  } catch (error) {
    const cached = yield call(getCachedTemplates, String(businessId));
    yield put(actions.getTemplatesSuccess(cached));
    const errorData = (error as AxiosError<CustomError>)?.response?.data;
    if (
      errorData &&
      !JSON.stringify(errorData).includes('ETIMEDOUT') &&
      !JSON.stringify(errorData).includes('ECONNABORTED')
    ) {
      yield put(actions.getTemplatesFailure(errorData));
      errorModal(errorData?.errorMessage || 'Failed to fetch templates');
    }
  }
}

export function* getFlows() {
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(api.get, `/flows`);
    localStorage.setItem(
      `flows_${businessId}`,
      JSON.stringify(response.data.data),
    );
    yield put(actions.getFlowsSuccess(response.data.data));
  } catch {
    yield put(
      actions.getFlowsSuccess(
        JSON.parse(localStorage.getItem(`flows_${businessId}`) || '[]'),
      ),
    );
  }
}

function* watchFetchClientListPage() {
  yield takeLatest(actions.fetchClientListPageStart.type, getClientListPage);
}

function* watchLoadMoreClientList() {
  yield takeLatest(actions.loadMoreClientListStart.type, loadMoreClientList);
}

function* watchFetchMessages() {
  yield takeLatest(actions.fetchMessagesStart.type, getMessages);
}

function* watchToggleChat() {
  yield takeLatest(actions.toggleClientStart.type, toggleChat);
}
function* watchToggleBotForClient() {
  yield takeLatest(actions.toggleBotForClientStart.type, toggleBotForClient);
}

function* watchToggleBlockClient() {
  yield takeLatest(actions.toggleBlockClientStart.type, toggleBlockClient);
}

function* watchClearSession() {
  yield takeLatest(actions.clearSessionStart.type, clearSession);
}

function* watchAddContacts() {
  yield takeLatest(actions.addContactsStart.type, addContacts);
}

function* watchUpdateClientAttributesAndTags() {
  yield takeLatest(
    actions.updateClientAttributesAndTagsStart.type,
    updateClientAttributesAndTags,
  );
}

function* watchSendMessages() {
  yield takeLatest(actions.sendMessagesStart.type, sendMessages);
}

function* watchFetchSingleClient() {
  yield takeLatest(actions.fetchSingleClientStart.type, getSingleClient);
}

function* watchGetTemplateMediaLink() {
  yield takeLatest(actions.getTemplateMediaLinkStart.type, getTemplateLink);
}

function* watchBulkAddTemplateMediaLink() {
  yield takeLatest(
    actions.bulkAddTemplateMediaLink.type,
    bulkAddTemplateMediaLink,
  );
}

function* watchAddTemplateMediaLink() {
  yield takeLatest(actions.addTemplateMediaLink.type, addTemplateLink);
}

function* watchGetTemplates() {
  yield takeLatest(actions.getTemplatesStart.type, getTemplates);
}

function* watchGetFlows() {
  yield takeLatest(actions.getFlowsStart.type, getFlows);
}

export function* inboxPageSaga() {
  const sagas = [
    watchFetchClientListPage,
    watchLoadMoreClientList,
    watchFetchMessages,
    watchToggleChat,
    watchToggleBotForClient,
    watchToggleBlockClient,
    watchClearSession,
    watchAddContacts,
    watchUpdateClientAttributesAndTags,
    watchSendMessages,
    watchFetchSingleClient,
    watchGetTemplateMediaLink,
    watchBulkAddTemplateMediaLink,
    watchAddTemplateMediaLink,
    watchGetTemplates,
    watchGetFlows,
  ];

  yield all(
    sagas.map(saga =>
      fork(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in inboxPagaSaga:', e);
          }
        }
      }),
    ),
  );
}
